import { useMemo } from "react";
import { useSelector, shallowEqual } from "react-redux";

const normalizeDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const extractEntityRecord = (entry) => {
  if (!entry) return null;
  const source =
    entry.corporate ||
    entry.company ||
    entry.entity ||
    entry.profile ||
    entry.details ||
    entry;

  if (!source) return null;

  const id =
    source._id ||
    source.id ||
    source.corporateId ||
    source.companyId ||
    source.organizationId ||
    source.code ||
    source.registrationNumber ||
    source.companyRegistrationNumber ||
    source.referenceNumber ||
    source.uuid;

  const name =
    source.name ||
    source.companyName ||
    source.corporateName ||
    source.legalName ||
    source.displayName ||
    source.entityName;

  if (!id || !name) {
    return null;
  }

  const registration =
    source.companyRegistrationNumber ||
    source.registrationNumber ||
    source.companyNumber ||
    source.registrationNo ||
    source.referenceNumber ||
    source.regNumber ||
    source.registration_id ||
    "";

  const incorporationDate =
    source.dateOfRegistration ||
    source.registrationDate ||
    source.dateOfIncorporation ||
    source.incorporationDate ||
    source.incorporatedOn ||
    source.registration_on ||
    "";

  return {
    id: String(id),
    name: String(name),
    registrationNumber: registration ? String(registration) : "",
    incorporationDate: normalizeDate(incorporationDate),
  };
};

const ensureArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const useEntityDirectoryOptions = () => {
  const directories = useSelector(
    (state) => ({
      organizations: state.organizationsSlice?.organizations?.items ?? [],
      organizationDashboard:
        state.organizationDashboardSlice?.organizations?.items ?? [],
      corporates: state.corporatesForDashboardSlice?.corporates ?? [],
      companies: state.companyDashboardSlice?.companies?.items ?? [],
    }),
    shallowEqual
  );

  const options = useMemo(() => {
    const collected = [];
    const seen = new Set();

    const pushOption = (option) => {
      if (!option || !option.id || seen.has(option.id)) {
        return;
      }
      seen.add(option.id);
      collected.push(option);
    };

    [
      ensureArray(directories.organizations),
      ensureArray(directories.organizationDashboard),
      ensureArray(
        Array.isArray(directories.corporates?.items)
          ? directories.corporates.items
          : directories.corporates
      ),
      ensureArray(
        Array.isArray(directories.companies?.items)
          ? directories.companies.items
          : directories.companies
      ),
    ].forEach((group) => {
      group.forEach((entry) => {
        const option = extractEntityRecord(entry);
        if (option) {
          pushOption(option);
        }
      });
    });

    return collected;
  }, [
    directories.organizations,
    directories.organizationDashboard,
    directories.corporates,
    directories.companies,
  ]);

  return options;
};

export default useEntityDirectoryOptions;
